import * as React from 'react'
import { connect } from 'react-redux'
import {
  unstable_HistoryRouter as HistoryRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom'

import ProjectDashboard from '../pages/ProjectsDashboard/Main'
import CreativeDetails from '../pages/CreativeDetails/Main'
import CreativePreview from '../pages/CreativePreview/Main'
import ProjectViewLayout from '../pages/ProjectView/Main'
import UserManagement from '../pages/UserManagement/Main'
import DI from './di'

import { CssBaseline, Drawer, IconButton, styled } from '@design/pt-ui'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { User } from '../interfaces/entities/user'
import { State } from '../model'
import { getAppEnv } from '../utils/config'

const Home = () => <Navigate to="/projects" replace={true} />

// These page components are connect()-wrapped and declare some props that the
// router never supplied (they were undefined under react-router v4's loosely
// typed `component` prop). Render them with no required props to match that.
const route = (C: React.ComponentType<any>) => React.createElement(C)

const drawerWidth = 286
const drawerClosedWidth = 30

const Root = styled('div')({
  display: 'flex',
  height: '100%',
})

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  width: open ? drawerWidth : drawerClosedWidth,
  '& .MuiDrawer-paper': {
    position: 'fixed',
    height: '100%',
    overflowX: 'hidden',
    width: open ? drawerWidth : drawerClosedWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
  },
}))

const DrawerToggle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
})

const CloseButton = styled(IconButton)({
  padding: 0,
  height: '100%',
  borderRadius: 0,
  width: '100%',
})

const Content = styled('main', {
  shouldForwardProp: (prop) => prop !== 'drawerOpen',
})<{ drawerOpen: boolean }>(({ theme, drawerOpen }) => ({
  flexGrow: 1,
  padding: theme.spacing(1),
  overflowX: 'hidden',
  transition: theme.transitions.create('margin-left', {
    easing: theme.transitions.easing.sharp,
    duration: drawerOpen
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  }),
}))

export function Layout(props: { user: User }) {
  const { user } = props

  const [open, setOpen] = React.useState(false)
  const isDev = getAppEnv().appEnv === 'development'
  const history = DI.get('history')

  return (
    <Root>
      <CssBaseline />
      {isDev ? (
        <StyledDrawer variant="permanent" open={open}>
          <DrawerToggle>
            <CloseButton onClick={() => setOpen((prev) => !prev)}>
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </CloseButton>
          </DrawerToggle>
        </StyledDrawer>
      ) : null}
      <Content drawerOpen={isDev ? open : false}>
        <HistoryRouter history={history}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/projects" element={route(ProjectDashboard)} />
            {!user.restrictedAccess ? (
              <Route path="/user-management" element={route(UserManagement)} />
            ) : null}
            <Route path="/project/:id" element={route(ProjectViewLayout)} />
            <Route path="/creatives/:id" element={route(CreativeDetails)} />
            <Route path="/creative-preview/:id" element={<CreativePreview />} />
          </Routes>
        </HistoryRouter>
      </Content>
    </Root>
  )
}

export default connect((state: State) => ({ user: state.user }))(Layout)
